import type { ZodSchemaOf } from "@@/shared/zod-helpers";
import type { Branded } from "@@/utilities/branded";
import { zoneDateUtils, ZonedDate } from "@@/utilities/zoned-date";
import { isValid, parseISO } from "date-fns";
import { toDate } from "date-fns-tz";
import { z } from "zod";
import { asDate } from "./as-date";
import type { FullDate } from "./full-date";
import { isDate } from "./is-date";
import {
    endOfDayZoned,
    endOfMonthZoned,
    startOfDayZoned,
    startOfMonthZoned,
} from "./start-and-end-of-day-zoned";
import type { Timezone } from "./timezone";
import { tz } from "./timezone";
import type { UnixTimestamp } from "./unix";
import { toUnix } from "./unix";

type d = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type YYYY = `19${Exclude<d, 0 | 1 | 2 | 3 | 4 | 5>}${d}` | `20${d}${d}`;
type MM = `0${Exclude<d, 0>}` | `1${0 | 1 | 2}`;
type ISO8601 = `${YYYY}-${MM}-${string}T${string}Z`; // To complex: `${YYYY}-${MM}-${DD}T${hh}:${mm}:${ss}.000Z`
type ISO8601_DATE_ONLY = `${YYYY}-${MM}-${number}`; // Too slow: `${YYYY}-${MM}-${DD}`

// DOESN'T WORK, TO COMPLEX:
// type DD = `0${Exclude<d, 0>}` | `1${d}` | `2${d}` | `3${0 | 1}`;
// type hh = `0${d}` | `1${d}` | `2${d}` | `3${d}` | `4${d}` | `5${d}`
// type mm = `0${d}` | `1${d}` | `2${d}` | `3${d}` | `4${d}` | `5${d}`
// type ss = `0${d}` | `1${d}` | `2${d}` | `3${d}` | `4${d}` | `5${d}`

function isoAndUnixFactory(date: IsoAndUnixTimestamp): IsoAndUnixTimestamp;
function isoAndUnixFactory(date: ZonedDate): IsoAndUnixTimestamp;
function isoAndUnixFactory(date: Date): IsoAndUnixTimestamp;
function isoAndUnixFactory(date: FullDate): IsoAndUnixTimestamp;
function isoAndUnixFactory(
    date: IsoAndUnixTimestamp | FullDate,
): IsoAndUnixTimestamp;
function isoAndUnixFactory(date: FullDate | Date): IsoAndUnixTimestamp;
function isoAndUnixFactory(
    date: Date | IsoAndUnixTimestamp,
): IsoAndUnixTimestamp;
function isoAndUnixFactory(
    date: Date | IsoAndUnixTimestamp | FullDate,
): IsoAndUnixTimestamp;
function isoAndUnixFactory(
    date: Date | IsoAndUnixTimestamp | FullDate,
): IsoAndUnixTimestamp {
    const _date = typeof date === "number" ? asDate(date) : date;

    if (_date instanceof ZonedDate) {
        // If the date is a zoned date, convert it to a regular date
        // that is, a zoned date has a timezone attached to it
        // and it's utc time is modified to reflect a specific timezone
        // so we need to convert it to a regular date in the date environments
        // real timezone to get the correct utc time
        const realDate = zoneDateUtils.fromZonedDate(_date);
        return {
            iso: realDate.toISOString() as IsoTimestamp,
            unix: toUnix(realDate),
        };
    }
    if (isDate(_date)) {
        return {
            iso: _date.toISOString() as IsoTimestamp,
            unix: toUnix(_date),
        };
    }

    return {
        iso: _date.iso,
        unix: _date.unix,
    };
}

type IsoTimestamp = Branded<string, "IsoTimestamp">;
/** @deprecated just use `IsoAndUnixTimestamp` instead */
type WithUnixTimestamp = {
    unix: UnixTimestamp;
};
/** @deprecated just use `IsoAndUnixTimestamp` instead */
type WithIsoTimestamp = {
    iso: IsoTimestamp;
};
type IsoAndUnixTimestamp = {
    unix: UnixTimestamp;
    iso: IsoTimestamp;
};

// This regex matches ISO8601 formats including:
// - 2025-03-06T15:17:17.784Z (milliseconds with Z)
// - 2025-03-06T15:17:17.784419 (microseconds without Z)
// - 2025-03-06T15:17:17.784419Z (microseconds with Z)
const isoRegExp =
    /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3,6})Z?/;

const isoDateOnlyRegExp = /(\d{4})-(\d{2})-(\d{2})/;

const unixTimestampZodSchema = z.coerce
    .number()
    .int()
    .positive() as unknown as ZodSchemaOf<UnixTimestamp>;
const isoTimestampZodSchema = z
    .string()
    .regex(isoRegExp) as unknown as ZodSchemaOf<IsoTimestamp>;
const isoAndUnixZodSchema: ZodSchemaOf<IsoAndUnixTimestamp> = z
    .object({
        iso: isoTimestampZodSchema,
        unix: unixTimestampZodSchema,
    })
    .refine(value => {
        const { iso, unix } = value;
        const isoDate = parseISO(iso);
        return toUnix(isoDate) === unix;
    });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIsoAndUnix = (item: any): item is IsoAndUnixTimestamp => {
    if (typeof item !== "object") return false;
    return typeof item.unix === "number" && typeof item.iso === "string";
};

const isIsoTimestamp = (item: unknown): item is IsoTimestamp => {
    if (typeof item !== "string") return false;
    return isValid(new Date(item));
};

const nowIsoAndUnix = (): IsoAndUnixTimestamp => isoAndUnixFactory(new Date());
const getZonedDateZodSchema = (
    timeZone: Timezone,
    options?: {
        startOrEnd?: "start" | "end";
        granularity?: "day" | "month";
    },
) =>
    z.preprocess(arg => {
        if (
            typeof arg == "string" ||
            typeof arg === "number" ||
            arg instanceof Date
        ) {
            const input = isDate(arg)
                ? arg
                : typeof arg === "string" && /^\d+$/.test(arg)
                  ? Number(arg)
                  : arg;
            if (options?.granularity === "month") {
                return options?.startOrEnd === "end"
                    ? endOfMonthZoned(toDate(input, { timeZone }), timeZone)
                    : startOfMonthZoned(toDate(input, { timeZone }), timeZone);
            }
            if (options?.granularity === "day") {
                return options?.startOrEnd === "end"
                    ? endOfDayZoned(toDate(input, { timeZone }), timeZone)
                    : startOfDayZoned(toDate(input, { timeZone }), timeZone);
            }
            return toDate(input, { timeZone });
        }
        // console.log(
        //     "DATE PARSE",
        //     arg,
        //     isDate(arg)
        //         ? arg
        //         : typeof arg === "string" && /^\d+$/.test(arg)
        //         ? Number(arg)
        //         : arg
        // );
        throw new Error("Invalid date; " + JSON.stringify(arg));
    }, z.date());
const utcDateZodSchema = getZonedDateZodSchema(tz.UTC);
const swedishDateZodSchema = getZonedDateZodSchema(tz.EuropeStockholm);

const isIsoAndUnixIsEqual = (a: IsoAndUnixTimestamp, b: IsoAndUnixTimestamp) =>
    a.unix === b.unix && a.iso === b.iso;

export {
    getZonedDateZodSchema,
    isIsoAndUnix,
    isIsoAndUnixIsEqual,
    isIsoTimestamp,
    isoAndUnixFactory,
    isoAndUnixZodSchema,
    isoDateOnlyRegExp,
    isoRegExp,
    isoTimestampZodSchema,
    nowIsoAndUnix,
    swedishDateZodSchema,
    unixTimestampZodSchema,
    utcDateZodSchema,
};
export type {
    d,
    ISO8601,
    ISO8601_DATE_ONLY,
    IsoAndUnixTimestamp,
    IsoTimestamp,
    MM,
    WithIsoTimestamp,
    WithUnixTimestamp,
    YYYY,
};
